// ============================================================
// Besprechungsprotokoll-Service — bearbeitbare, speicherbare
// Protokolle, optional mit einem Kalender-Termin (event_id) und/oder
// einer Firma (mandant_id) verknüpft. Voll synchronisiert.
// ============================================================

import { db, type Protokoll, type ProtokollAufgabe } from '../db/schema';
import { logAktion } from './auditLogService';

function jetzt(): string { return new Date().toISOString(); }
function heute(): string { return new Date().toISOString().slice(0, 10); }

export function neuesProtokoll(init?: Partial<Protokoll>): Protokoll {
  return {
    id: crypto.randomUUID(),
    schema_version: 1,
    titel: init?.titel ?? '',
    datum: init?.datum ?? heute(),
    teilnehmer: init?.teilnehmer ?? '',
    notizen: init?.notizen ?? '',
    aufgaben: init?.aufgaben ?? [],
    anhaenge: init?.anhaenge ?? [],
    erstellt_am: jetzt(),
    geaendert_am: jetzt(),
    ...(init?.uhrzeit ? { uhrzeit: init.uhrzeit } : {}),
    ...(init?.uhrzeit_ende ? { uhrzeit_ende: init.uhrzeit_ende } : {}),
    ...(init?.ort ? { ort: init.ort } : {}),
    ...(init?.mandant_id ? { mandant_id: init.mandant_id } : {}),
    ...(init?.event_id ? { event_id: init.event_id } : {}),
    ...(init?.naechste_sitzung ? { naechste_sitzung: init.naechste_sitzung } : {}),
  };
}

export function neueAufgabe(text = ''): ProtokollAufgabe {
  return { id: crypto.randomUUID(), text, erledigt: false };
}

/** Alle Protokolle (optional nach Firma gefiltert), neueste zuerst. */
export async function loadProtokolle(mandantId?: string): Promise<Protokoll[]> {
  let all = await db.protokolle.toArray();
  if (mandantId !== undefined && mandantId !== '') all = all.filter(p => p.mandant_id === mandantId);
  return all.sort((a, b) =>
    b.datum.localeCompare(a.datum) || b.erstellt_am.localeCompare(a.erstellt_am));
}

export async function getProtokoll(id: string): Promise<Protokoll | undefined> {
  return db.protokolle.get(id);
}

/** Bereits existierendes Protokoll zu einem Kalender-Termin finden. */
export async function findByEvent(eventId: string): Promise<Protokoll | undefined> {
  const all = await db.protokolle.toArray();
  return all.find(p => p.event_id === eventId);
}

/** Set der event_ids, für die ein Protokoll existiert (für Kalender-Anzeige). */
export async function eventIdsMitProtokoll(): Promise<Set<string>> {
  const all = await db.protokolle.toArray();
  const s = new Set<string>();
  for (const p of all) if (p.event_id !== undefined && p.event_id !== '') s.add(p.event_id);
  return s;
}

/** Speichern (create/update) — leere Aufgaben werden verworfen. */
export async function saveProtokoll(p: Protokoll): Promise<void> {
  const bereinigt: Protokoll = {
    ...p,
    titel: p.titel.trim() || 'Besprechung',
    aufgaben: p.aufgaben.filter(a => a.text.trim() !== ''),
    geaendert_am: jetzt(),
  };
  await db.protokolle.put(bereinigt);
  void logAktion(p.mandant_id ?? 'SYSTEM', 'PROTOKOLL_GESPEICHERT');
}

/** Löschen (erzeugt via Sync-Middleware einen Tombstone). */
export async function deleteProtokoll(id: string): Promise<void> {
  await db.protokolle.delete(id);
}

/** Einen Anhang aus einem Protokoll entfernen. */
export async function removeAnhang(protokollId: string, anhangId: string): Promise<void> {
  const p = await db.protokolle.get(protokollId);
  if (p === undefined) return;
  await saveProtokoll({ ...p, anhaenge: p.anhaenge.filter(a => a.id !== anhangId) });
}
